import { useEffect, useState } from "react";
import { TextField, Button, Snackbar, Alert, MenuItem } from "@mui/material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { getCurrentUser } from "../utils/currentUser";
import {
    getEmployees,
    addEmployee,
    updateEmployee,
    deleteEmployee
} from "../services/employeeService";

import { getBranches } from "../services/branchService";

import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

// ✅ FIXED PATHS (common folder)
import ActionButtons from "../components/common/ActionButtons";
import DateCell from "../components/common/DateCell";

// ✅ utilities
//import { logAudit } from "../utils/auditHelper";
import { formatEmployeeLabel } from "../utils/formatters";

// ✅ hooks
import usePagination from "../hooks/usePagination";
function Employee() {
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [employees, setEmployees] = useState([]);
    const [branches, setBranches] = useState([]);
    const [originalEmployee, setOriginalEmployee] = useState(null);
    const [search, setSearch] = useState("");
    
    const [showInactive, setShowInactive] = useState(false);

    const [form, setForm] = useState({
        id: 0,
        empCode: "",
        empName: "",
        address: "",
        mobileNo: "",
        emailId: "",
        designation: "",
        dob: "",
        doj: "",
        reportingManager: "",
        branchId: "",
        status: "Active"
    });

    const [isEdit, setIsEdit] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });
    const user = getCurrentUser();

    const canDelete =
        user?.role?.toUpperCase() === "ADMIN";


    // ✅ FIRST define function
    const loadData = () => {
        getEmployees().then(res => setEmployees(res.data || []));
        getBranches().then(res => setBranches(res.data || []));
    };

    // ✅ ADD THIS HELPER (instead of wrong updated block)
    const buildEmployeePayload = (emp, status) => ({
        id: emp.id,
        empCode: emp.empCode,
        empName: emp.empName,
        branchId: emp.branchId,
        address: emp.address,
        mobileNo: emp.mobileNo,
        emailId: emp.emailId,
        designation: emp.designation,
        dob: emp.dob,
        doj: emp.doj,
        reportingManager: emp.reportingManager,
        Status: status   // ✅ FIXED
    });

    // ✅ THEN use it
    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (!isEdit) {
            const newCode = generateEmpCode();

            setForm(prev => {
                if (prev.empCode === newCode || prev.id !== 0) return prev;

                return {
                    ...prev,
                    empCode: newCode
                };
            });
        }
    }, [employees, isEdit]);
    const [sortField, setSortField] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(prev => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortField(field);
            setSortDirection("asc");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };
    const generateEmpCode = () => {
        if (!employees.length) return "EMP001";

        const numbers = employees
            .map(e => e.empCode)
            .filter(code => /^EMP\d+$/.test(code))
            .map(code => parseInt(code.replace("EMP", ""), 10));

        const max = Math.max(...numbers, 0);

        return `EMP${(max + 1).toString().padStart(3, "0")}`;
    };
    const handleSave = async () => {

        if (!form.empCode) {
            return setSnackbar({
                open: true,
                message: "Employee Code not generated. Please wait or refresh.",
                severity: "error"
            });
        }
        // 🔥 VALIDATIONS
        if (!form.branchId) {
            return setSnackbar({
                open: true,
                message: "Please select Branch",
                severity: "error"
            });
        }

        if (form.empName && form.empName.length > 100) {
            return setSnackbar({ open: true, message: "Name max 100 characters", severity: "error" });
        }

        if (form.mobileNo && form.mobileNo.length > 15) {
            return setSnackbar({ open: true, message: "Mobile max 15 digits", severity: "error" });
        }

        if (form.mobileNo && !/^[0-9]{10}$/.test(form.mobileNo)) {
            return setSnackbar({ open: true, message: "Mobile number must be 10 digits", severity: "error" });
        }

        if (form.emailId && form.emailId.length > 100) {
            return setSnackbar({ open: true, message: "Email max 100 characters", severity: "error" });
        }

        if (form.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailId)) {
            return setSnackbar({ open: true, message: "Invalid email format", severity: "error" });
        }

        const payload = {
            empCode: form.empCode, 
            empName: form.empName,
            branchId: form.branchId ? Number(form.branchId) : null,
            address: form.address || null,
            mobileNo: form.mobileNo || null,
            emailId: form.emailId || null,
            designation: form.designation || null,
            dob: form.dob ? new Date(form.dob).toISOString() : null,
            doj: form.doj ? new Date(form.doj).toISOString() : null,
            reportingManager: form.reportingManager || null,
            status: form.status || "Active"
        };

        try {
            const res = isEdit
                ? await updateEmployee(form.id, payload)
                : await addEmployee(payload);

            const label = formatEmployeeLabel(form);

            const newData = payload;
            const oldData = isEdit ? originalEmployee : null;

            //logAudit(
            //    "Employee",
            //    isEdit ? "Update" : "Create",
            //    `Employee ${label} ${isEdit ? "updated" : "created"}`,
            //    isEdit ? form.id : res?.data?.id || 0,
            //    oldData,
            //    newData
            //);

            setSnackbar({
                open: true,
                message: isEdit ? "Updated Successfully" : "Saved Successfully",
                severity: "success"
            });

            resetForm();
            loadData();

        } catch (err) {
            console.log("ERROR:", err?.response?.data);

            setSnackbar({
                open: true,
                message:
                    err?.response?.data?.title ||
                    Object.values(err?.response?.data?.errors || {})?.flat()?.[0] ||
                    "Error",
                severity: "error"
            });
        }
    };
    const resetForm = () => {
        setForm({
            id: 0,
            empCode: "",
            empName: "",
            address: "",
            mobileNo: "",
            emailId: "",
            designation: "",
            dob: "",
            doj: "",
            reportingManager: "",
            branchId: "",
            status: "Active"
        });
        setIsEdit(false);
    };

    const handleEdit = (e) => {
        setOriginalEmployee(e);   // 🔥 ADD THIS LINE
        setForm({
            ...e,

            // 🔥 FIX DATE FORMAT HERE
            dob: e.dob ? e.dob.split("T")[0] : "",
            doj: e.doj ? e.doj.split("T")[0] : ""
        });

        setIsEdit(true);
    };
    const handleDelete = async (emp) => {
        if (emp?.status === "Inactive") return;

        if (!window.confirm("Deactivate this employee?")) return;

        try {
            const updated = buildEmployeePayload(emp, "Inactive");

            await updateEmployee(emp.id, updated);

            const label = formatEmployeeLabel(emp);

            setSnackbar({
                open: true,
                message: `${label} deactivated`,
                severity: "success"
            });

            loadData();
        } catch (err) {
            console.error(err);
        }
    };

    {/* ✅ ADD HERE (exact place) */ }
    const handleRestore = async (emp) => {
        try {
            const updated = buildEmployeePayload(emp, "Active");

            await updateEmployee(emp.id, updated);

            setSnackbar({
                open: true,
                message: "Employee restored",
                severity: "success"
            });

            loadData();
        } catch (err) {
            console.error(err);
        }
    };

    const handlePermanentDelete = async (emp) => {
        if (!window.confirm(`Permanently delete "${emp.empName}"?`)) return;

        try {
            await deleteEmployee(emp.id);

            const label = formatEmployeeLabel(emp);

            //logAudit(
            //    "Employee",
            //    "Permanent Delete",
            //    `Employee ${label} permanently deleted`,
            //    emp.id,
            //    emp,
            //    null
            //);

            setSnackbar({
                open: true,
                message: "Employee permanently deleted",
                severity: "error"
            });

            loadData();

        } catch (err) {
            console.error("Permanent delete failed:", err);
        }
    };
    const filteredData = (employees || []).filter(e => {

        // Hide inactive unless checkbox ON
        if (!showInactive && e.status === "Inactive") return false;

        return (
            (e.empName || "").toLowerCase().includes(search.toLowerCase()) ||
            (e.empCode || "").toLowerCase().includes(search.toLowerCase())
        );
    });

    // 🔽 SORT
    const sortedData = [...filteredData];

    if (sortField) {
        sortedData.sort((a, b) => {
            let valA = (a[sortField] || "").toString().toLowerCase();
            let valB = (b[sortField] || "").toString().toLowerCase();

            if (valA < valB) return sortDirection === "asc" ? -1 : 1;
            if (valA > valB) return sortDirection === "asc" ? 1 : -1;
            return 0;
        });
    }   // ✅ VERY IMPORTANT
    const {
        page,
        setPage,
        totalPages,
        paginatedData
    } = usePagination(sortedData, rowsPerPage);
   
    // 📤 EXPORT
    const exportToExcel = () => {
        const data = filteredData.map(e => ({
            Code: e.empCode,
            Name: e.empName,
            Address: e.address,
            Mobile: e.mobileNo,
            Email: e.emailId,
            Branch: branches.find(b => b.id === e.branchId)?.branchName
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employees");

        const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "Employees.xlsx");
    };
    return (
        <div style={styles.container}>

            <div style={styles.header}>
                <h2>Employee Master</h2>
            </div>

            {/* FORM */}
            {/*<LocalizationProvider dateAdapter={AdapterDayjs}>*/}
                <div style={styles.card}>
                    <div style={styles.formContainer}>

                        {/* ROW 1 */}
                        <div style={styles.row}>
                            <TextField
                                label="Emp Code"
                                name="empCode"
                                value={form.empCode}
                                fullWidth
                                size="small"
                                sx={styles.inputStyle}
                                InputProps={{ readOnly: true }}   // ✅ lock it
                            />
                            <TextField label="Emp Name" name="empName" value={form.empName} onChange={handleChange} fullWidth size="small" sx={styles.inputStyle} />
                        </div>

                        {/* ROW 2 */}
                        <div style={{ ...styles.row, alignItems: "stretch" }}>

                            {/* LEFT SIDE (Address) */}
                            <div style={{ flex: 1, display: "flex" }}>
                                <TextField
                                    label="Address"
                                    name="address"
                                    value={form.address}
                                    onChange={handleChange}
                                    fullWidth
                                    multiline
                                    sx={{
                                        ...styles.inputStyle,
                                        flex: 1,
                                        "& .MuiInputBase-root": {
                                            height: "100%",          // ✅ stretch full height
                                            alignItems: "flex-start" // ✅ text starts from top
                                        }
                                    }}
                                />
                            </div>

                            {/* RIGHT SIDE */}
                            <div style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                flex: 1
                            }}>
                                <TextField
                                    label="Designation"
                                    name="designation"
                                    value={form.designation}
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    sx={styles.inputStyle}
                                />

                                <TextField
                                    label="Mobile"
                                    name="mobileNo"
                                    value={form.mobileNo}
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    sx={styles.inputStyle}
                                />

                                <TextField
                                    label="Manager"
                                    name="reportingManager"
                                    value={form.reportingManager}
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    sx={styles.inputStyle}
                                />
                            </div>

                        </div>

                        {/* ROW 3 */}
                        <div style={styles.row}>

                            {/* LEFT SIDE (same width as Address) */}
                            <div style={{ flex: 1, display: "flex", gap: "10px" }}>

                                <TextField
                                    label="DOB"
                                    type="date"
                                    name="dob"
                                    value={form.dob || ""}
                                    onChange={handleChange}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />

                                <TextField
                                    label="DOJ"
                                    type="date"
                                    name="doj"
                                    value={form.doj || ""}
                                    onChange={handleChange}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                />

                            </div>

                            {/* RIGHT SIDE (same width as Manager column) */}
                            <div style={{ flex: 1 }}>
                                <TextField
                                    label="Email"
                                    name="emailId"
                                    value={form.emailId}
                                    onChange={handleChange}
                                    fullWidth
                                    size="small"
                                    sx={styles.inputStyle}
                                />
                            </div>

                        </div>
                        {/* ✅ ROW 4 (NEW - THIS IS YOUR FIX) */}
                        <div style={styles.row}>

                            <TextField
                                select
                                label="Branch"
                                name="branchId"
                                value={form.branchId}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                sx={styles.inputStyle}
                            >
                                {branches.map(b => (
                                    <MenuItem
                                        key={b.id}
                                        value={b.id}
                                        sx={{ fontFamily: "Verdana", fontSize: "12px" }}
                                    >
                                        {b.branchName}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                label="Status"
                                name="status"
                                value={form.status}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                sx={styles.inputStyle}
                            >
                                <MenuItem value="Active" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                    Active
                                </MenuItem>

                                <MenuItem value="Inactive" sx={{ fontFamily: "Verdana", fontSize: "12px" }}>
                                    Inactive
                                </MenuItem>
                            </TextField>

                        </div>

                    </div>

                    <div style={styles.buttonRow}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            sx={{
                                height: "28px",
                                minHeight: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                padding: "0 12px",
                                textTransform: "none !important"
                            }}
                        >
                            {isEdit ? "Update" : "Save"}
                        </Button>

                        {isEdit && (
                            <Button
                                variant="contained"
                                onClick={resetForm}
                                sx={{
                                    height: "28px",
                                    minHeight: "28px",
                                    fontSize: "12px",
                                    fontFamily: "Verdana",
                                    padding: "0 12px",
                                    textTransform: "none !important",
                                    marginLeft: "10px",
                                    background: "#2f3542",   // 🔵 dark color (or use same as update if you want)
                                    color: "#fff",
                                    "&:hover": {
                                        background: "#1e272e"
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                    </div>
                </div>
            {/*</LocalizationProvider>*/}
            {/* GRID */}
            <div style={styles.card}>

                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%"
                }}>
                    {/* ✅ ADD THIS FIRST */}
                    <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "5px" }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                        />
                        Show Inactive
                    </label>
                    {/* 🔍 Search Box (FIXED) */}
                    <TextField
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{
                            width: "220px",

                            "& .MuiOutlinedInput-root": {
                                height: "28px",
                                fontFamily: "Verdana"
                            },

                            "& .MuiOutlinedInput-input": {
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontFamily: "Verdana"
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: search && (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearch("")}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    {/* 📤 Export */}
                    <Button
                        variant="contained"
                        onClick={exportToExcel}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            background: "#3742fa"
                        }}
                    >
                        Export
                    </Button>

                    {/* 📄 Rows */}
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                        <span style={{ fontSize: "12px" }}>Rows</span>
                        <select
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                            style={{
                                height: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "0 5px"
                            }}
                        >
                            {[5, 10, 20, 30, 50].map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>

                </div>

                <div style={{ ...styles.tableWrapper, marginTop: "10px" }}>
                    <table style={styles.table}>
                        <thead style={styles.tableHeader}>
                            <tr>
                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("empCode")}
                                >
                                    Code {sortField === "empCode" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("empName")}
                                >
                                    Name {sortField === "empName" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th style={styles.th}>Address</th>

                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("mobileNo")}
                                >
                                    Mobile {sortField === "mobileNo" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("emailId")}
                                >
                                    Email {sortField === "emailId" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>

                                <th style={styles.th}>Branch</th>
                                {/* ✅ ADD THESE TWO */}
                                <th style={styles.th}>Created On</th>
                                <th style={styles.th}>Updated On</th>
                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("status")}
                                >
                                    Status {sortField === "status" ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
                                </th>
                                                                
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map(e => (
                                <tr key={e.id}>
                                    <td style={styles.td}>{e.empCode}</td>
                                    <td style={styles.td}>{e.empName}</td>
                                    <td style={styles.td}>{e.address}</td>
                                    <td style={styles.td}>{e.mobileNo}</td>
                                    <td style={styles.td}>{e.emailId}</td>

                                    <td style={styles.td}>
                                        {branches.find(b => b.id === e.branchId)?.branchName}
                                    </td>
                                    {/* ✅ ADD HERE */}
                                    <td style={styles.td}>
                                        <DateCell value={e.createdOn} />
                                    </td>

                                    <td style={styles.td}>
                                        <DateCell value={e.updatedOn} />
                                    </td>

                                    {/* ✅ ADD THIS */}
                                    <td style={styles.td}>
                                        {e.status}
                                    </td>

                                    {/* ✅ ADD THIS */}
                                    <td style={{ ...styles.td, textAlign: "center" }}>
                                        <ActionButtons
                                            row={e}
                                            status={e.status}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onPermanentDelete={handlePermanentDelete}
                                            onRestore={() => handleRestore(e)}
                                            canEdit={true}
                                            canDelete={true}
                                        />
                                    </td>

                                </tr>
                            ))}
                        </tbody>

                    </table>
                    <div style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "10px",
                        marginTop: "10px"
                    }}>
                        <button
                            onClick={() => setPage(prev => Math.max(prev - 1, 0))}
                            disabled={page === 0}
                        >
                            Prev
                        </button>

                        <span style={{ fontSize: "12px", fontFamily: "Verdana" }}>
                            Page {page + 1} of {totalPages}
                        </span>

                        <button
                            onClick={() => setPage(prev => prev + 1)}
                            disabled={page + 1 >= totalPages}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
            <Snackbar open={snackbar.open} autoHideDuration={3000}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </div>
    );
}

const styles = {
    container: {
        padding: "5px",
        background: "#f1f2f6",
        fontFamily: "Verdana",
        fontSize: "12px"
    },

    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },

    card: {
        background: "#fff",
        padding: "15px",
        borderRadius: "15px",
        marginBottom: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
    },

    formContainer: { display: "flex", flexDirection: "column", gap: "10px" },
    row: { display: "flex", gap: "10px" },
    rightColumn: { display: "flex", flexDirection: "column", gap: "10px", flex: 1 },

    tableWrapper: { width: "100%", overflowX: "auto" },

    table: {
        width: "100%",
        borderCollapse: "collapse"
    },

    tableHeader: {
        background: "#57606f",
        color: "#fff"
    },

    // 🔥 UPDATED HEADER STYLE (important)
    th: {
        border: "1px solid #ccc",
        padding: "8px",
        backgroundColor: "#5f6b7a",
        color: "#fff",
        fontWeight: "bold",
        textAlign: "left",
        userSelect: "none",
        whiteSpace: "nowrap"
    },

    td: {
        border: "1px solid #ddd",
        padding: "8px",
        verticalAlign: "middle"
    },

    // 🔥 BUTTONS IMPROVED
    editBtn: {
        background: "#2ed573",
        border: "none",
        padding: "6px",
        borderRadius: "4px",
        cursor: "pointer",
        marginRight: "5px"
    },

    deleteBtn: {
        background: "#ff4757",
        border: "none",
        padding: "6px",
        borderRadius: "4px",
        cursor: "pointer"
    },

    restoreBtn: {
        backgroundColor: "#3b82f6",
        color: "#fff",
        border: "none",
        padding: "6px",
        marginLeft: "5px",
        cursor: "pointer",
        borderRadius: "4px"
    },

    search: {
        padding: "6px 10px",
        height: "28px",
        borderRadius: "4px",
        border: "1px solid #ccc"
    },

    buttonRow: {
        marginTop: "10px"
    },

    // 🔥 NEW: Pagination (you didn’t have this)
    pagination: {
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: "10px",
        marginTop: "10px"
    },

    inputStyle: {
        "& .MuiInputBase-input": {
            fontFamily: "Verdana",
            fontSize: "12px"
        },
        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        }
    }
};

export default Employee;